import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";

function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat(Infinity).join(" ");
  }

  return fallback;
}

function formatDateTime(value) {
  if (!value) {
    return "Sin información";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Sin información";
  }

  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getStatusConfig(accessPoint) {
  const status =
    accessPoint.connection_status ||
    accessPoint.status ||
    "UNKNOWN";

  if (status === "ONLINE") {
    return {
      label: "En línea",
      color: "#047857",
      backgroundColor: "#ecfdf5",
    };
  }

  if (status === "ERROR") {
    return {
      label: "Error",
      color: "#b00020",
      backgroundColor: "#fff1f2",
    };
  }

  if (status === "DISABLED") {
    return {
      label: "Desactivado",
      color: "#6b7280",
      backgroundColor: "#f3f4f6",
    };
  }

  if (status === "OFFLINE") {
    return {
      label: "Fuera de línea",
      color: "#b45309",
      backgroundColor: "#fffbeb",
    };
  }

  return {
    label: "Desconocido",
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
  };
}

function getSyncConfig(accessPoint) {
  const syncStatus = accessPoint.sync_status || "PENDING";

  if (syncStatus === "SYNCED") {
    return {
      label: "Sincronizado",
      color: "#047857",
      backgroundColor: "#ecfdf5",
    };
  }

  if (syncStatus === "ERROR") {
    return {
      label: "Error",
      color: "#b00020",
      backgroundColor: "#fff1f2",
    };
  }

  return {
    label: "Pendiente",
    color: "#b45309",
    backgroundColor: "#fffbeb",
  };
}

export default function AccessPoints() {
  const { fetchUser } = useAuth();

  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accessPoints, setAccessPoints] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailAccessPoint, setDetailAccessPoint] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showFeedback = useCallback((severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const loadData = useCallback(
    async (showLoader = true) => {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [organizationResponse, accessPointsResponse] =
          await Promise.all([
            fetchUser("/tenants/api/logo/", {
              method: "GET",
              cache: "no-store",
            }),
            fetchUser("/access-control/api/web/access-points/", {
              method: "GET",
              cache: "no-store",
            }),
          ]);

        const organizationData = await readJson(organizationResponse);
        const accessPointsData = await readJson(accessPointsResponse);

        if (
          !organizationResponse.ok ||
          organizationData.ok !== true ||
          !organizationData.tenant
        ) {
          throw new Error(
            getApiMessage(
              organizationData,
              "No se pudo cargar la organización."
            )
          );
        }

        if (
          !accessPointsResponse.ok ||
          accessPointsData.ok !== true
        ) {
          throw new Error(
            getApiMessage(
              accessPointsData,
              "No se pudieron cargar los accesos."
            )
          );
        }

        setOrganization(organizationData.tenant);

        setAccessPoints(
          Array.isArray(accessPointsData.access_points)
            ? accessPointsData.access_points
            : []
        );
      } catch (error) {
        showFeedback(
          "error",
          error instanceof Error
            ? error.message
            : "No se pudo conectar con el servidor."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchUser, showFeedback]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredAccessPoints = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return accessPoints;
    }

    return accessPoints.filter((accessPoint) => {
      return (
        String(accessPoint.name || "")
          .toLowerCase()
          .includes(query) ||
        String(
          accessPoint.brand_name ||
            accessPoint.brand ||
            ""
        )
          .toLowerCase()
          .includes(query) ||
        String(accessPoint.serial_number || "")
          .toLowerCase()
          .includes(query) ||
        String(accessPoint.ip_address || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [accessPoints, search]);

  const paginatedAccessPoints = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredAccessPoints.slice(
      start,
      start + rowsPerPage
    );
  }, [filteredAccessPoints, page, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(
        filteredAccessPoints.length / rowsPerPage
      ) - 1
    );

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [
    filteredAccessPoints.length,
    page,
    rowsPerPage,
  ]);

  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1300,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: {
                      xs: 26,
                      md: 32,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Accesos
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {accessPoints.length} equipos registrados
                </Typography>
              </Box>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: {
                  xs: 2,
                  sm: 2.5,
                },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por nombre, marca, serial o dirección IP"
              />
            </Box>

            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Acceso</TableCell>
                      <TableCell>Marca</TableCell>
                      <TableCell>Dirección IP</TableCell>
                      <TableCell>Conexión</TableCell>
                      <TableCell>Sincronización</TableCell>
                      <TableCell>Permisos</TableCell>
                      <TableCell>Última conexión</TableCell>
                      <TableCell align="right">
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedAccessPoints.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Box
                            sx={{
                              minHeight: 240,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              padding: 3,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 17,
                                fontWeight: 800,
                              }}
                            >
                              No existen accesos registrados
                            </Typography>

                            <Typography
                              sx={{
                                color: "#6b7280",
                                fontSize: 13,
                                marginTop: 0.8,
                                maxWidth: 500,
                              }}
                            >
                              Registra los biométricos en la aplicación
                              local para que aparezcan en este panel.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedAccessPoints.map(
                        (accessPoint) => {
                          const statusConfig =
                            getStatusConfig(accessPoint);

                          const syncConfig =
                            getSyncConfig(accessPoint);

                          const permissionsCount =
                            Number(
                              accessPoint.group_permissions_count ||
                                0
                            ) +
                            Number(
                              accessPoint.person_permissions_count ||
                                0
                            );

                          return (
                            <TableRow
                              key={accessPoint.id}
                              hover
                            >
                              <TableCell>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 800,
                                  }}
                                >
                                  {accessPoint.name}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                  }}
                                >
                                  {accessPoint.brand_name ||
                                    accessPoint.brand ||
                                    "Sin marca"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                  }}
                                >
                                  {accessPoint.ip_address ||
                                    "Sin dirección IP"}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={statusConfig.label}
                                  sx={{
                                    fontWeight: 800,
                                    color: statusConfig.color,
                                    backgroundColor:
                                      statusConfig.backgroundColor,
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <Chip
                                  size="small"
                                  label={syncConfig.label}
                                  sx={{
                                    fontWeight: 800,
                                    color: syncConfig.color,
                                    backgroundColor:
                                      syncConfig.backgroundColor,
                                  }}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: 800,
                                  }}
                                >
                                  {permissionsCount}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{ fontSize: 12 }}
                                >
                                  {formatDateTime(
                                    accessPoint.last_seen_at
                                  )}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setDetailAccessPoint(
                                      accessPoint
                                    )
                                  }
                                >
                                  Ver detalles
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none",
                },
                padding: 2,
              }}
            >
              {paginatedAccessPoints.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 220,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    No existen accesos registrados
                  </Typography>

                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 13,
                      marginTop: 0.8,
                    }}
                  >
                    Registra los biométricos desde la aplicación local.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedAccessPoints.map(
                    (accessPoint) => {
                      const statusConfig =
                        getStatusConfig(accessPoint);

                      const syncConfig =
                        getSyncConfig(accessPoint);

                      const permissionsCount =
                        Number(
                          accessPoint.group_permissions_count ||
                            0
                        ) +
                        Number(
                          accessPoint.person_permissions_count ||
                            0
                        );

                      return (
                        <Box
                          key={accessPoint.id}
                          sx={{
                            padding: 2,
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 1,
                              marginBottom: 1.5,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  fontWeight: 800,
                                  wordBreak: "break-word",
                                }}
                              >
                                {accessPoint.name}
                              </Typography>

                              <Typography
                                sx={{
                                  color: "#6b7280",
                                  fontSize: 12,
                                  marginTop: 0.3,
                                }}
                              >
                                {accessPoint.brand_name ||
                                  accessPoint.brand ||
                                  "Sin marca"}
                              </Typography>
                            </Box>

                            <Chip
                              size="small"
                              label={statusConfig.label}
                              sx={{
                                flexShrink: 0,
                                fontWeight: 800,
                                color: statusConfig.color,
                                backgroundColor:
                                  statusConfig.backgroundColor,
                              }}
                            />
                          </Box>

                          <Typography sx={{ fontSize: 13 }}>
                            <strong>Dirección IP:</strong>{" "}
                            {accessPoint.ip_address ||
                              "Sin dirección IP"}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              marginTop: 0.6,
                            }}
                          >
                            <strong>Sincronización:</strong>{" "}
                            <Box
                              component="span"
                              sx={{
                                color: syncConfig.color,
                                fontWeight: 800,
                              }}
                            >
                              {syncConfig.label}
                            </Box>
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              marginTop: 0.6,
                            }}
                          >
                            <strong>Permisos:</strong>{" "}
                            {permissionsCount}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              marginTop: 0.6,
                              marginBottom: 1.5,
                            }}
                          >
                            <strong>Última conexión:</strong>{" "}
                            {formatDateTime(
                              accessPoint.last_seen_at
                            )}
                          </Typography>

                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setDetailAccessPoint(
                                accessPoint
                              )
                            }
                          >
                            Ver detalles
                          </Button>
                        </Box>
                      );
                    }
                  )}
                </Stack>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredAccessPoints.length}
              page={page}
              onPageChange={(_, newPage) =>
                setPage(newPage)
              }
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(
                  Number(event.target.value)
                );
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={Boolean(detailAccessPoint)}
        onClose={() =>
          setDetailAccessPoint(null)
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Información del acceso
        </DialogTitle>

        <DialogContent>
          {detailAccessPoint && (
            <Stack
              spacing={1.5}
              sx={{ paddingTop: 1 }}
            >
              {[
                [
                  "Nombre",
                  detailAccessPoint.name ||
                    "Sin nombre",
                ],
                [
                  "Marca",
                  detailAccessPoint.brand_name ||
                    detailAccessPoint.brand ||
                    "Sin marca",
                ],
                [
                  "Número de serie",
                  detailAccessPoint.serial_number ||
                    "Sin número de serie",
                ],
                [
                  "Dirección IP",
                  detailAccessPoint.ip_address ||
                    "Sin dirección IP",
                ],
                [
                  "Última conexión",
                  formatDateTime(
                    detailAccessPoint.last_seen_at
                  ),
                ],
                [
                  "Última sincronización",
                  formatDateTime(
                    detailAccessPoint.last_synced_at
                  ),
                ],
                [
                  "Permisos por rol",
                  String(
                    detailAccessPoint.group_permissions_count ||
                      0
                  ),
                ],
                [
                  "Permisos individuales",
                  String(
                    detailAccessPoint.person_permissions_count ||
                      0
                  ),
                ],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    paddingBottom: 1.2,
                    borderBottom:
                      "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      marginTop: 0.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                      marginBottom: 0.7,
                    }}
                  >
                    Estado de conexión
                  </Typography>

                  <Chip
                    size="small"
                    label={
                      getStatusConfig(
                        detailAccessPoint
                      ).label
                    }
                    sx={{
                      fontWeight: 800,
                      color:
                        getStatusConfig(
                          detailAccessPoint
                        ).color,
                      backgroundColor:
                        getStatusConfig(
                          detailAccessPoint
                        ).backgroundColor,
                    }}
                  />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                      marginBottom: 0.7,
                    }}
                  >
                    Estado de sincronización
                  </Typography>

                  <Chip
                    size="small"
                    label={
                      getSyncConfig(
                        detailAccessPoint
                      ).label
                    }
                    sx={{
                      fontWeight: 800,
                      color:
                        getSyncConfig(
                          detailAccessPoint
                        ).color,
                      backgroundColor:
                        getSyncConfig(
                          detailAccessPoint
                        ).backgroundColor,
                    }}
                  />
                </Box>
              </Box>

              {detailAccessPoint.last_error && (
                <Alert severity="error" variant="outlined" sx={{backgroundColor:"#fef2f2",borderColor:"#fecaca",color:"#b91c1c",fontWeight:700,"& .MuiAlert-icon":{color:"#dc2626"}}}>
                  {detailAccessPoint.last_error}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setDetailAccessPoint(null)
            }
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3500}
        onClose={() => setFeedback((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ marginTop: 1 }}
      >
        <Alert
          severity={feedback.severity}
          variant="outlined"
          onClose={() => setFeedback((previous) => ({ ...previous, open: false }))}
          sx={{
            minWidth: { xs: "calc(100vw - 32px)", sm: 340 },
            maxWidth: 460,
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            fontWeight: 700,
            backgroundColor: feedback.severity === "success" ? "#f0fdf4" : "#fef2f2",
            borderColor: feedback.severity === "success" ? "#bbf7d0" : "#fecaca",
            color: feedback.severity === "success" ? "#166534" : "#b91c1c",
            "& .MuiAlert-icon": {
              color: feedback.severity === "success" ? "#16a34a" : "#dc2626",
            },
            "& .MuiAlert-action": {
              color: feedback.severity === "success" ? "#166534" : "#b91c1c",
            },
          }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}